/**
 * PortalAttribution — Click-level revenue attribution dashboard
 *
 * Shows per-click ROI: source → affiliate → sub_id → creative → decision → outcome → revenue
 * Full waterfall from traffic source to money earned.
 */

import React, { useState, useEffect, useCallback } from 'react';
import PortalLayout from '@/components/portal/PortalLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Cell } from 'recharts';
import { Crosshair, Loader2, DollarSign, TrendingUp } from 'lucide-react';
import { supabaseGet } from '@/utils/supabaseRest';

interface AttributionRow {
  session_id: string;
  affiliate_id: string | null;
  sub_id: string | null;
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
  device_type: string | null;
  geo_state: string | null;
}

interface ScoringRow {
  session_id: string;
  intent_score: number;
  monetization_score: number;
  decision: string;
}

interface OutcomeRow {
  session_id: string;
  revenue: number;
  outcome_status: string;
}

export default function PortalAttribution() {
  const [attributions, setAttributions] = useState<AttributionRow[]>([]);
  const [scores, setScores] = useState<ScoringRow[]>([]);
  const [outcomes, setOutcomes] = useState<OutcomeRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [groupBy, setGroupBy] = useState<'affiliate_id' | 'utm_source' | 'utm_campaign' | 'device_type' | 'geo_state'>('utm_source');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [a, s, o] = await Promise.all([
        supabaseGet('source_attribution?order=created_at.desc&limit=1000'),
        supabaseGet('scoring_results?select=session_id,intent_score,monetization_score,decision&limit=1000'),
        supabaseGet('outcomes?select=session_id,revenue,outcome_status&limit=1000'),
      ]);
      setAttributions(a || []);
      setScores(s || []);
      setOutcomes(o || []);
    } catch (err) {
      console.error('Failed to load attribution:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Join data
  const scoreMap = new Map(scores.map(s => [s.session_id, s]));
  const outcomeMap = new Map(outcomes.map(o => [o.session_id, o]));

  const joined = attributions.map(a => ({
    ...a,
    score: scoreMap.get(a.session_id),
    outcome: outcomeMap.get(a.session_id),
  }));

  // Group by selected dimension
  const grouped = joined.reduce<Record<string, { sessions: number; revenue: number; conversions: number }>>((acc, row) => {
    const key = (row as any)[groupBy] || 'unknown';
    if (!acc[key]) acc[key] = { sessions: 0, revenue: 0, conversions: 0 };
    acc[key].sessions++;
    if (row.outcome?.outcome_status === 'converted') {
      acc[key].conversions++;
      acc[key].revenue += Number(row.outcome.revenue || 0);
    }
    return acc;
  }, {});

  const chartData = Object.entries(grouped)
    .map(([name, data]) => ({
      name: name.length > 15 ? name.slice(0, 15) + '...' : name,
      revenue: Math.round(data.revenue * 100) / 100,
      sessions: data.sessions,
      cvr: data.sessions > 0 ? Math.round(data.conversions / data.sessions * 1000) / 10 : 0,
    }))
    .sort((a, b) => b.revenue - a.revenue)
    .slice(0, 15);

  const totalRevenue = Object.values(grouped).reduce((s, g) => s + g.revenue, 0);
  const totalSessions = attributions.length;
  const totalConversions = Object.values(grouped).reduce((s, g) => s + g.conversions, 0);

  return (
    <PortalLayout>
      <div className="max-w-6xl mx-auto space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <Crosshair className="h-6 w-6 text-emerald-400" />
            Click Attribution
          </h1>
          <p className="text-sm text-slate-400 mt-1">Per-click revenue attribution. See exactly which source made money.</p>
        </div>

        {/* Group by selector */}
        <div className="flex gap-2 flex-wrap">
          {([
            ['utm_source', 'Source'],
            ['utm_campaign', 'Campaign'],
            ['affiliate_id', 'Affiliate'],
            ['device_type', 'Device'],
            ['geo_state', 'State'],
          ] as const).map(([key, label]) => (
            <button
              key={key}
              onClick={() => setGroupBy(key)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                groupBy === key ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/40' : 'bg-slate-800 text-slate-400 border border-slate-700'
              }`}
            >{label}</button>
          ))}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20"><Loader2 className="h-8 w-8 text-emerald-400 animate-spin" /></div>
        ) : (
          <>
            {/* KPIs */}
            <div className="grid grid-cols-3 gap-3">
              <Card className="bg-slate-800/50 border-slate-700">
                <CardContent className="p-4 text-center">
                  <div className="text-2xl font-bold text-white">{totalSessions}</div>
                  <div className="text-xs text-slate-400">Tracked Sessions</div>
                </CardContent>
              </Card>
              <Card className="bg-slate-800/50 border-slate-700">
                <CardContent className="p-4 text-center">
                  <div className="text-2xl font-bold text-emerald-400">${totalRevenue.toFixed(2)}</div>
                  <div className="text-xs text-slate-400">Attributed Revenue</div>
                </CardContent>
              </Card>
              <Card className="bg-slate-800/50 border-slate-700">
                <CardContent className="p-4 text-center">
                  <div className="text-2xl font-bold text-blue-400">{totalSessions > 0 ? (totalConversions / totalSessions * 100).toFixed(1) : 0}%</div>
                  <div className="text-xs text-slate-400">Overall CVR</div>
                </CardContent>
              </Card>
            </div>

            {/* Revenue by group */}
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm text-slate-300">Revenue by {groupBy.replace(/_/g, ' ')}</CardTitle>
              </CardHeader>
              <CardContent>
                {chartData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={chartData} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                      <XAxis type="number" tick={{ fill: '#94a3b8', fontSize: 12 }} />
                      <YAxis type="category" dataKey="name" tick={{ fill: '#94a3b8', fontSize: 11 }} width={120} />
                      <Tooltip contentStyle={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 8 }} />
                      <Bar dataKey="revenue" name="Revenue ($)" fill="#10b981" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-[200px] flex items-center justify-center text-sm text-slate-500">No attribution data yet</div>
                )}
              </CardContent>
            </Card>

            {/* Detail table */}
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader className="pb-2"><CardTitle className="text-sm text-slate-300">Attribution Breakdown</CardTitle></CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-slate-500 border-b border-slate-700">
                        <th className="text-left py-2 px-2">{groupBy.replace(/_/g, ' ')}</th>
                        <th className="text-right py-2 px-2">Sessions</th>
                        <th className="text-right py-2 px-2">Conversions</th>
                        <th className="text-right py-2 px-2">CVR</th>
                        <th className="text-right py-2 px-2">Revenue</th>
                        <th className="text-right py-2 px-2">Rev/Session</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(grouped).sort((a, b) => b[1].revenue - a[1].revenue).map(([key, data]) => (
                        <tr key={key} className="border-b border-slate-800 hover:bg-slate-800/50">
                          <td className="py-2 px-2 text-white">{key || '—'}</td>
                          <td className="py-2 px-2 text-right text-slate-300">{data.sessions}</td>
                          <td className="py-2 px-2 text-right text-slate-300">{data.conversions}</td>
                          <td className="py-2 px-2 text-right text-slate-300">{data.sessions > 0 ? (data.conversions / data.sessions * 100).toFixed(1) : 0}%</td>
                          <td className="py-2 px-2 text-right font-medium text-emerald-400">${data.revenue.toFixed(2)}</td>
                          <td className="py-2 px-2 text-right text-slate-300">${data.sessions > 0 ? (data.revenue / data.sessions).toFixed(2) : '0.00'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </PortalLayout>
  );
}
